import type {
  ConfigurationScope,
  ConfigurationScopeId,
  WriteConfigurationScopeRequest,
} from "@thalamos/haven-common";

import type { ErrorBody } from "../../api/axios/api.js";
import api from "../../api/index.js";

/** How creating, renaming or moving a configuration went. */
export type ScopeWriteOutcome =
  | { kind: "saved"; scope: ConfigurationScope }
  | { kind: "refused"; message: string | null };

/**
 * How submitting the configuration form went: written, or a move the page is
 * asking the administrator to confirm before anything is sent.
 */
export type ScopeFormOutcome =
  ScopeWriteOutcome | { kind: "awaiting_confirmation" };

/**
 * Sends a configuration's name and parent, creating it when no id is given.
 *
 * A 4xx resolves (`validateStatus` in `api/axios/api.ts`); only a 5xx or a
 * dropped connection rejects, and that is a refusal with nothing to say.
 */
export const writeScope = async ({
  scopeId,
  body,
}: {
  /** Null to create one. */
  scopeId: ConfigurationScopeId | null;
  body: WriteConfigurationScopeRequest;
}): Promise<ScopeWriteOutcome> => {
  try {
    const response =
      scopeId === null
        ? await api.configuration.createScope(body)
        : await api.configuration.updateScope(scopeId, body);

    if (response.status >= 400 || !("id" in response.data)) {
      const failure = response.data as ErrorBody;
      return { kind: "refused", message: failure.message ?? null };
    }

    return { kind: "saved", scope: response.data };
  } catch {
    return { kind: "refused", message: null };
  }
};
